/**
 * PipCharacter - Premium SVG-based animated character
 * 
 * Features:
 * - Smooth SVG animations
 * - Multiple moods and expressions
 * - Action animations (eat, drink, wave, celebrate)
 * - Speech bubble integration
 * - Holding items
 */

import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import './PipCharacter.css';

const PipCharacter = forwardRef(({
  mood = 'happy',      // happy, excited, curious, sleepy
  animation = 'idle',  // idle, bounce, celebrate, wave, eat, drink
  holding = null,      // 'juice', 'apple', or emoji string
  bubble = '',         // Speech bubble text
  size = 'medium',     // small, medium, large
}, ref) => {
  const [internalAnim, setInternalAnim] = useState(animation);

  useEffect(() => {
    setInternalAnim(animation);
  }, [animation]);

  // Expose methods
  useImperativeHandle(ref, () => ({
    celebrate: () => {
      setInternalAnim('celebrate');
      setTimeout(() => setInternalAnim('idle'), 2000);
    },
    wave: () => {
      setInternalAnim('wave');
      setTimeout(() => setInternalAnim('idle'), 2000);
    },
    eat: () => {
      setInternalAnim('eat');
      setTimeout(() => setInternalAnim('idle'), 1500);
    },
    drink: () => {
      setInternalAnim('drink');
      setTimeout(() => setInternalAnim('idle'), 1500);
    },
  }));

  // Get holding item emoji
  const getHoldingEmoji = () => {
    if (!holding) return null;
    if (holding === 'juice') return '🧃';
    if (holding === 'apple') return '🍎';
    if (holding === 'milk') return '🥛';
    return holding;
  };

  const holdingEmoji = getHoldingEmoji();

  return (
    <div className={`pip pip--${size} pip--${internalAnim} pip--mood-${mood}`}>
      {/* Speech Bubble */}
      {bubble && (
        <div className="pip__bubble">
          <span className="pip__bubble-text">{bubble}</span>
          <div className="pip__bubble-tail" />
        </div>
      )}

      {/* Held Item */}
      {holdingEmoji && (
        <div className="pip__holding">{holdingEmoji}</div>
      )}

      {/* Character SVG */}
      <svg 
        className="pip__svg" 
        viewBox="0 0 120 140" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Body gradient */}
          <linearGradient id="pipBodyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fcd34d" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
          
          {/* Shine gradient */}
          <linearGradient id="pipShineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.4)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          
          {/* Shadow filter */}
          <filter id="pipShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#f59e0b" floodOpacity="0.4"/>
          </filter>
        </defs>

        {/* Body */}
        <g className="pip__body-group" filter="url(#pipShadow)">
          <ellipse 
            className="pip__body"
            cx="60" 
            cy="65" 
            rx="42" 
            ry="45" 
            fill="url(#pipBodyGrad)"
          />
          
          {/* Body shine */}
          <ellipse 
            cx="45" 
            cy="50" 
            rx="18" 
            ry="20" 
            fill="url(#pipShineGrad)"
          />
        </g>

        {/* Left Arm */}
        <g className="pip__arm pip__arm--left">
          <ellipse 
            cx="22" 
            cy="70" 
            rx="10" 
            ry="16" 
            fill="url(#pipBodyGrad)"
          />
        </g>

        {/* Right Arm */}
        <g className="pip__arm pip__arm--right">
          <ellipse 
            cx="98" 
            cy="70" 
            rx="10" 
            ry="16" 
            fill="url(#pipBodyGrad)"
          />
        </g>

        {/* Face Group */}
        <g className="pip__face">
          {/* Left Eye */}
          <g className="pip__eye pip__eye--left">
            <ellipse 
              className="pip__eye-white"
              cx="45" 
              cy="55" 
              rx="10" 
              ry={mood === 'excited' ? 12 : 10}
              fill="#1e293b"
            />
            <ellipse 
              className="pip__pupil"
              cx="45" 
              cy="55" 
              rx="5" 
              ry="5" 
              fill="#0f172a"
            />
            <circle 
              className="pip__eye-shine"
              cx="48" 
              cy="52" 
              r="3" 
              fill="white"
            />
          </g>

          {/* Right Eye */}
          <g className="pip__eye pip__eye--right">
            <ellipse 
              className="pip__eye-white"
              cx="75" 
              cy="55" 
              rx="10" 
              ry={mood === 'excited' ? 12 : 10}
              fill="#1e293b"
            />
            <ellipse 
              className="pip__pupil"
              cx="75" 
              cy="55" 
              rx="5" 
              ry="5" 
              fill="#0f172a"
            />
            <circle 
              className="pip__eye-shine"
              cx="78" 
              cy="52" 
              r="3" 
              fill="white"
            />
          </g>

          {/* Cheeks */}
          <ellipse 
            className="pip__cheek pip__cheek--left"
            cx="32" 
            cy="72" 
            rx="8" 
            ry="5" 
            fill="#f97316" 
            opacity="0.6"
          />
          <ellipse 
            className="pip__cheek pip__cheek--right"
            cx="88" 
            cy="72" 
            rx="8" 
            ry="5" 
            fill="#f97316" 
            opacity="0.6"
          />

          {/* Mouth */}
          <g className="pip__mouth">
            {mood === 'excited' ? (
              // Big smile
              <path 
                d="M 48 80 Q 60 95, 72 80" 
                fill="#1e293b"
                stroke="none"
              />
            ) : mood === 'curious' ? (
              // Small O
              <ellipse cx="60" cy="82" rx="6" ry="6" fill="#1e293b" />
            ) : (
              // Normal smile
              <path 
                d="M 50 78 Q 60 88, 70 78" 
                fill="none"
                stroke="#1e293b"
                strokeWidth="4"
                strokeLinecap="round"
              />
            )}
          </g>
        </g>

        {/* Feet */}
        <g className="pip__feet">
          <ellipse 
            className="pip__foot pip__foot--left"
            cx="45" 
            cy="115" 
            rx="14" 
            ry="8" 
            fill="url(#pipBodyGrad)"
          />
          <ellipse 
            className="pip__foot pip__foot--right"
            cx="75" 
            cy="115" 
            rx="14" 
            ry="8" 
            fill="url(#pipBodyGrad)"
          />
        </g>
      </svg>

      {/* Celebration particles */}
      {internalAnim === 'celebrate' && (
        <div className="pip__particles">
          {['⭐', '✨', '🌟', '💫', '🎉', '✨', '⭐', '💫'].map((emoji, i) => (
            <span 
              key={i}
              className="pip__particle"
              style={{
                '--delay': `${i * 0.1}s`,
                '--angle': `${(i / 8) * 360}deg`,
                '--distance': `${60 + Math.random() * 30}px`,
              }}
            >
              {emoji}
            </span>
          ))}
        </div>
      )}
    </div>
  );
});

PipCharacter.displayName = 'PipCharacter';

export default PipCharacter;
